import { Component, forwardRef, inject, input, signal, OnDestroy, ElementRef, HostListener } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
} from '@angular/forms';

import { LucideIconComponent } from '../../lucide-icon/lucide-icon.component';
import { Subject, debounceTime, switchMap, takeUntil, tap, distinctUntilChanged } from 'rxjs';
import { CompanySearchService, CompanySearchResult, CompanyReference } from '../../../../core/services/company-search.service';
import { TextComponent } from '../../typography/text/text.component';
import { BadgeComponent } from '../../badge/badge.component';
import { ColorVariant } from '../../../../core/enums/types';
import { ModalService } from '../../../../core/services/modal.service';
import { DotSearchModalComponent } from './dot-search-modal/dot-search-modal.component';

@Component({
  selector: 'app-company-search',
  standalone: true,
  imports: [
    FormsModule,
    LucideIconComponent,
    TextComponent,
    BadgeComponent
],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CompanySearchComponent),
      multi: true,
    },
  ],
  templateUrl: './company-search.component.html',
  styleUrls: ['./company-search.component.css'],
  host: { class: 'block relative' },
})
export class CompanySearchComponent implements ControlValueAccessor, OnDestroy {
  private readonly searchService = inject(CompanySearchService);
  private readonly modalService = inject(ModalService);
  private readonly elRef = inject(ElementRef);
  private readonly destroy$ = new Subject<void>();
  private readonly searchInput$ = new Subject<string>();

  // Inputs
  placeholder = input<string>('Search by name, DOT, or MC...');

  // State
  searchText = signal('');
  results = signal<CompanySearchResult[]>([]);
  isOpen = signal(false);
  isLoading = signal(false);
  selectedCompany = signal<CompanyReference | null>(null);
  disabled = signal(false);

  // CVA
  onChange: (value: CompanyReference | null) => void = () => {};
  onTouched: () => void = () => {};

  constructor() {
    // Wire up the debounced search
    this.searchInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => this.isLoading.set(true)),
        switchMap((query) => this.searchService.searchCompanies(query)),
        takeUntil(this.destroy$)
      )
      .subscribe((results) => {
        this.results.set(results);
        this.isLoading.set(false);
        this.isOpen.set(true);
      });
  }

  // --- ControlValueAccessor ---

  writeValue(value: CompanyReference | null): void {
    this.selectedCompany.set(value);
    if (value) {
      this.searchText.set(value.legalName);
    } else {
      this.searchText.set('');
    }
  }

  registerOnChange(fn: (value: CompanyReference | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // --- User Interactions ---

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchText.set(value);

    if (value.length >= 2) {
      this.searchInput$.next(value);
    } else {
      this.results.set([]);
      this.isOpen.set(false);
    }

    // If the user is typing, clear the selected company
    if (this.selectedCompany()) {
      this.selectedCompany.set(null);
      this.onChange(null);
    }
  }

  onFocus(): void {
    this.onTouched();
    if (this.searchText().length >= 2 && this.results().length > 0) {
      this.isOpen.set(true);
    }
  }

  selectCompany(result: CompanySearchResult): void {
    const ref = this.searchService.toReference(result);
    this.selectedCompany.set(ref);
    this.searchText.set(result.legalName);
    this.isOpen.set(false);
    this.onChange(ref);
  }

  clearSelection(): void {
    this.selectedCompany.set(null);
    this.searchText.set('');
    this.results.set([]);
    this.isOpen.set(false);
    this.onChange(null);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.isOpen.set(false);
    }
  }

  getSourceBadgeVariant(source: string): ColorVariant {
    switch (source) {
      case 'haulverse':
        return 'success';
      case 'dot':
        return 'info';
      case 'manual':
        return 'secondary';
      default:
        return 'secondary';
    }
  }

  getSourceLabel(source: string): string {
    switch (source) {
      case 'haulverse':
        return 'Haulverse';
      case 'dot':
        return 'DOT';
      case 'manual':
        return 'Manual';
      default:
        return source;
    }
  }

  openDotSearchModal(): void {
    // Close the dropdown first
    this.isOpen.set(false);

    this.modalService.open(DotSearchModalComponent, {
      title: 'Search DOT Registry',
      data: {
        initialName: this.searchText(),
        onSuccess: (result: CompanySearchResult) => {
          this.selectCompany(result);
        },
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
